import React, { useState, useEffect, useMemo } from 'react';
import {
  Check, ArrowRight, Users, Clock, Headphones, Info, AlertCircle,
  Sparkles, TrendingUp, PiggyBank, Zap, LayoutDashboard, Globe, Rocket
} from 'lucide-react';
import { Card } from './ui/card';
import { Button } from './ui/button';
import { useTranslation } from 'react-i18next';
import OfferModal from './OfferModal';
import { CALCULATOR_SERVICES } from '../data/calculatorServices';
import { useAnimatedNumber } from '../hooks/useAnimatedNumber';

const SERVICE_ICONS = { Users, LayoutDashboard, Zap, Globe };

const formatSwiss = (n) => Math.round(n).toString().replace(/\B(?=(\d{3})+(?!\d))/g, "'");

const PremiumCalculator = () => {
  const { t } = useTranslation();

  const [serviceId, setServiceId] = useState(CALCULATOR_SERVICES[0].id);
  const service = useMemo(
    () => CALCULATOR_SERVICES.find((s) => s.id === serviceId),
    [serviceId]
  );

  // Base + addon state — keyed per service so switching keeps sensible defaults
  const [selectedBaseIds, setSelectedBaseIds] = useState(service.basePackage.map((b) => b.id));
  const [selectedAddonIds, setSelectedAddonIds] = useState(service.presets.business);
  const [employees, setEmployees] = useState(15);
  const [showMinHint, setShowMinHint] = useState(false);
  const [activePreset, setActivePreset] = useState('business');
  const [isOfferModalOpen, setIsOfferModalOpen] = useState(false);

  // When service changes, reset base to all + apply "business" preset for addons
  useEffect(() => {
    setSelectedBaseIds(service.basePackage.map((b) => b.id));
    setSelectedAddonIds(service.presets.business);
    setActivePreset('business');
  }, [serviceId, service.basePackage, service.presets.business]);

  const selectedBaseItems = service.basePackage.filter((b) => selectedBaseIds.includes(b.id));
  const selectedAddonItems = service.addons.filter((a) => selectedAddonIds.includes(a.id));

  const setupPrice =
    selectedBaseItems.reduce((s, i) => s + i.price, 0) +
    selectedAddonItems.reduce((s, i) => s + i.price, 0);

  const monthlyPrice = service.hasEmployeeSlider
    ? employees * service.monthlyPerEmployee
    : service.monthlyFixed;

  // Swiss agency benchmark
  const agencyPrice = Math.round(setupPrice * service.agencyMultiplier);
  const savings = agencyPrice - setupPrice;

  // ROI — hours saved & value per month
  const featuresCount = selectedBaseItems.length + selectedAddonItems.length;
  const employeeMultiplier = service.hasEmployeeSlider ? employees : 1;
  const hoursSavedPerMonth = featuresCount * service.hoursPerFeature * employeeMultiplier;
  const moneySavedPerMonth = hoursSavedPerMonth * service.hourlyRate;
  const paybackMonths = moneySavedPerMonth > 0
    ? Math.max(1, Math.ceil(setupPrice / moneySavedPerMonth))
    : 0;

  // Animated counters
  const animatedSetup = useAnimatedNumber(setupPrice);
  const animatedMonthly = useAnimatedNumber(monthlyPrice);
  const animatedSavings = useAnimatedNumber(savings);
  const animatedHours = useAnimatedNumber(hoursSavedPerMonth);
  const animatedMoney = useAnimatedNumber(moneySavedPerMonth);

  // Delivery estimate
  const deliveryDays = (() => {
    const n = selectedAddonIds.length;
    if (n <= 2) return '5–8';
    if (n <= 4) return '8–12';
    return '12–18';
  })();

  const toggleBase = (id) => {
    setSelectedBaseIds((prev) => {
      if (prev.includes(id)) {
        if (prev.length === 1) {
          setShowMinHint(true);
          setTimeout(() => setShowMinHint(false), 2500);
          return prev;
        }
        return prev.filter((x) => x !== id);
      }
      return [...prev, id];
    });
    setActivePreset(null);
  };

  const toggleAddon = (id) => {
    setSelectedAddonIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
    setActivePreset(null);
  };

  const applyPreset = (presetKey) => {
    setSelectedBaseIds(service.basePackage.map((b) => b.id));
    setSelectedAddonIds(service.presets[presetKey]);
    setActivePreset(presetKey);
  };

  const tService = (key) => t(`calculator.servicesList.${service.tKey}.${key}`);

  const getConfiguration = () => ({
    service: t(`calculator.servicesList.${service.tKey}.name`),
    serviceId: service.id,
    basePackage: selectedBaseItems.map((i) => ({ name: tService(i.tKey), price: i.price })),
    selectedAddons: selectedAddonItems.map((a) => ({ name: tService(a.tKey), price: a.price })),
    employees: service.hasEmployeeSlider ? employees : null,
    setupPrice,
    monthlyPrice,
    deliveryDays,
    agencyPrice,
    savings,
    hoursSavedPerMonth,
    moneySavedPerMonth,
    paybackMonths,
  });

  return (
    <section id="calculator" className="py-24 px-4 sm:px-6 lg:px-8 bg-gradient-to-b from-gray-50 via-white to-gray-50 dark:from-slate-900 dark:via-slate-950 dark:to-slate-900 relative overflow-hidden">
      <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjAiIGhlaWdodD0iNjAiIHZpZXdCb3g9IjAgMCA2MCA2MCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48ZyBmaWxsPSJub25lIiBmaWxsLXJ1bGU9ImV2ZW5vZGQiPjxnIGZpbGw9IiMyNTYzZWIiIGZpbGwtb3BhY2l0eT0iMC4wNSI+PHBhdGggZD0iTTM2IDE2YzAtMi4yMSAxLjc5LTQgNC00czQgMS43OSA0IDQtMS43OSA0LTQgNC00LTEuNzktNC00em0wIDI0YzAtMi4yMSAxLjc5LTQgNC00czQgMS43OSA0IDQtMS43OSA0LTQgNC00LTEuNzktNC00ek0xMiAxNmMwLTIuMjEgMS43OS00IDQtNHM0IDEuNzkgNCA0LTEuNzkgNC00IDQtNC0xLjc5LTQtNHptMCAyNGMwLTIuMjEgMS43OS00IDQtNHM0IDEuNzkgNCA0LTEuNzkgNC00IDQtNC0xLjc5LTQtNHoiLz48L2c+PC9nPjwvc3ZnPg==')] opacity-40"></div>

      <div className="max-w-6xl mx-auto relative z-10">
        {/* Header */}
        <div className="text-center mb-10">
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600/10 border border-blue-500/20 rounded-full mb-6">
            <div className="w-2 h-2 bg-blue-500 rounded-full animate-pulse"></div>
            <span className="text-sm text-blue-600 dark:text-blue-400 font-medium" data-testid="calculator-badge">
              {t('calculator.badge')}
            </span>
          </div>
          <h2 className="text-4xl md:text-5xl font-bold text-gray-900 dark:text-white mb-4" data-testid="calculator-title">
            {t('calculator.title')}
          </h2>
          <p className="text-lg text-gray-600 dark:text-gray-400 max-w-2xl mx-auto">{t('calculator.subtitle')}</p>
        </div>

        {/* Service Selector */}
        <div className="mb-8">
          <div className="flex items-center gap-2 mb-3">
            <Sparkles className="w-4 h-4 text-blue-600 dark:text-blue-400" />
            <h3 className="text-sm font-semibold text-gray-900 dark:text-white uppercase tracking-wider">
              {t('calculator.chooseService')}
            </h3>
          </div>
          <p className="text-sm text-gray-500 mb-4">{t('calculator.chooseServiceHint')}</p>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
            {CALCULATOR_SERVICES.map((s) => {
              const Icon = SERVICE_ICONS[s.icon] || Users;
              const isActive = s.id === serviceId;
              return (
                <button
                  key={s.id}
                  onClick={() => setServiceId(s.id)}
                  data-testid={`service-${s.id}`}
                  className={`group relative p-4 rounded-xl border-2 text-left transition-all duration-300 backdrop-blur-sm
                    ${isActive
                      ? 'bg-gradient-to-br from-blue-600/20 to-blue-500/10 border-blue-500 shadow-lg shadow-blue-500/30 scale-[1.02]'
                      : 'bg-white/70 dark:bg-slate-900/40 border-gray-200 dark:border-slate-700 hover:border-gray-400 dark:hover:border-slate-500 hover:bg-gray-50 dark:hover:bg-slate-800/50'
                    }`}
                >
                  <div className={`w-10 h-10 rounded-lg flex items-center justify-center mb-3 transition-all
                    ${isActive ? 'bg-blue-500 shadow-lg shadow-blue-500/50' : 'bg-gray-100 dark:bg-slate-800 group-hover:bg-gray-200 dark:group-hover:bg-slate-700'}`}>
                    <Icon className={`w-5 h-5 ${isActive ? 'text-white' : 'text-blue-600 dark:text-blue-400'}`} />
                  </div>
                  <div className={`text-sm sm:text-base font-semibold mb-1 break-words ${isActive ? 'text-gray-900 dark:text-white' : 'text-gray-800 dark:text-gray-200'}`}>
                    {t(`calculator.servicesList.${s.tKey}.name`)}
                  </div>
                  <div className="text-xs text-gray-500 leading-relaxed break-words">
                    {t(`calculator.servicesList.${s.tKey}.tagline`)}
                  </div>
                  {isActive && (
                    <div className="absolute top-2 right-2 w-2 h-2 rounded-full bg-blue-400 animate-pulse"></div>
                  )}
                </button>
              );
            })}
          </div>
        </div>

        {/* Preset Cards */}
        <div className="mb-8">
          <div className="flex items-center gap-2 mb-3">
            <Rocket className="w-4 h-4 text-blue-600 dark:text-blue-400" />
            <h3 className="text-sm font-semibold text-gray-900 dark:text-white uppercase tracking-wider">
              {t('calculator.quickPresets')}
            </h3>
          </div>
          <p className="text-sm text-gray-500 mb-4">{t('calculator.presetsHint')}</p>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            {['starter', 'business', 'enterprise'].map((key) => {
              const isActive = activePreset === key;
              const isPopular = key === 'business';
              return (
                <button
                  key={key}
                  onClick={() => applyPreset(key)}
                  data-testid={`preset-${key}`}
                  className={`relative p-4 rounded-xl border-2 text-left transition-all duration-300
                    ${isActive
                      ? 'bg-gradient-to-br from-blue-600/20 to-blue-500/5 border-blue-500 shadow-lg shadow-blue-500/20'
                      : 'bg-white/70 dark:bg-slate-900/40 border-gray-200 dark:border-slate-700 hover:border-gray-400 dark:hover:border-slate-500'
                    }`}
                >
                  {isPopular && (
                    <div className="absolute -top-2.5 right-3 px-2.5 py-0.5 bg-gradient-to-r from-orange-500 to-pink-500 rounded-full text-xs font-bold text-white shadow-lg">
                      {t('calculator.mostPopular')}
                    </div>
                  )}
                  <div className={`font-bold text-lg mb-1 ${isActive ? 'text-gray-900 dark:text-white' : 'text-gray-800 dark:text-gray-200'}`}>
                    {t(`calculator.presets.${key}.name`)}
                  </div>
                  <div className="text-xs text-gray-500">
                    {t(`calculator.presets.${key}.desc`)}
                  </div>
                </button>
              );
            })}
          </div>
        </div>

        <div className="grid lg:grid-cols-3 gap-8">
          {/* LEFT COL — Configuration */}
          <div className="lg:col-span-2 space-y-6">
            {/* Base Package */}
            <Card className="bg-gradient-to-br from-blue-600 to-blue-700 border-blue-500 shadow-2xl shadow-blue-500/20 overflow-hidden">
              <div className="p-6">
                <div className="flex items-center justify-between mb-4">
                  <div>
                    <h3 className="text-lg font-bold text-white mb-1">{t('calculator.basePackage')}</h3>
                    <p className="text-sm text-blue-100">{t('calculator.baseSubtitle')}</p>
                  </div>
                  <div className="px-3 py-1 bg-white/20 backdrop-blur-sm rounded-full">
                    <span className="text-xs font-semibold text-white">{t('calculator.included')}</span>
                  </div>
                </div>
                <div className="space-y-3">
                  {service.basePackage.map((item) => {
                    const isSelected = selectedBaseIds.includes(item.id);
                    return (
                      <button
                        key={item.id}
                        type="button"
                        onClick={() => toggleBase(item.id)}
                        data-testid={`base-toggle-${item.id}`}
                        className={`w-full flex items-center justify-between p-4 rounded-lg border transition-all duration-200 text-left
                          ${isSelected
                            ? 'bg-white/15 border-white/40 shadow-md'
                            : 'bg-white/5 border-white/10 hover:bg-white/10'
                          }`}
                      >
                        <div className="flex items-center gap-3 min-w-0 flex-1">
                          <div className={`w-5 h-5 rounded-full flex items-center justify-center transition-all flex-shrink-0
                            ${isSelected ? 'bg-white' : 'bg-white/20 border border-white/40'}`}>
                            {isSelected && <Check className="w-3 h-3 text-blue-600" strokeWidth={3} />}
                          </div>
                          <span className={`font-medium break-words ${isSelected ? 'text-white' : 'text-blue-100/70'}`}>
                            {tService(item.tKey)}
                          </span>
                        </div>
                        <span className={`font-bold whitespace-nowrap ml-2 ${isSelected ? 'text-white' : 'text-blue-100/70'}`}>
                          CHF {formatSwiss(item.price)}
                        </span>
                      </button>
                    );
                  })}
                </div>
                {showMinHint && (
                  <div
                    className="mt-3 flex items-center gap-2 p-3 bg-white/15 border border-white/30 rounded-lg text-sm text-white"
                    data-testid="base-min-hint"
                  >
                    <AlertCircle className="w-4 h-4 flex-shrink-0" />
                    <span>{t('calculator.baseMinHint')}</span>
                  </div>
                )}
              </div>
            </Card>

            {/* Additional Features */}
            <Card className="bg-white/80 dark:bg-slate-900/50 border-gray-200 dark:border-slate-800 shadow-xl backdrop-blur-sm">
              <div className="p-6">
                <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-4">
                  {t('calculator.additionalFeatures')}
                </h3>
                <div className="grid gap-3">
                  {service.addons.map((addon) => {
                    const isSelected = selectedAddonIds.includes(addon.id);
                    return (
                      <button
                        key={addon.id}
                        onClick={() => toggleAddon(addon.id)}
                        data-testid={`addon-toggle-${addon.id}`}
                        className={`relative flex items-start justify-between p-4 rounded-lg border-2 transition-all duration-200 text-left
                          ${isSelected
                            ? 'bg-blue-600/10 border-blue-500 shadow-lg shadow-blue-500/20'
                            : 'bg-gray-100 dark:bg-slate-800/50 border-gray-200 dark:border-slate-700 hover:border-gray-400 dark:hover:border-slate-600 hover:bg-gray-50 dark:hover:bg-slate-800'
                          }`}
                      >
                        {addon.popular && !isSelected && (
                          <div className="absolute -top-2 -right-2 px-2 py-1 bg-gradient-to-r from-orange-500 to-pink-500 rounded-full text-xs font-bold text-white shadow-lg">
                            {t('calculator.popular')}
                          </div>
                        )}
                        <div className="flex items-start gap-3 flex-1 min-w-0">
                          <div className={`w-6 h-6 rounded-lg border-2 flex items-center justify-center transition-all duration-200 flex-shrink-0 mt-0.5
                            ${isSelected ? 'bg-blue-600 border-blue-500' : 'border-gray-300 dark:border-slate-600'}`}>
                            {isSelected && <Check className="w-4 h-4 text-white" strokeWidth={3} />}
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 mb-1">
                              <span className={`font-medium break-words ${isSelected ? 'text-gray-900 dark:text-white' : 'text-gray-700 dark:text-gray-300'}`}>
                                {tService(addon.tKey)}
                              </span>
                              <div className="group relative flex-shrink-0">
                                <Info className={`w-4 h-4 ${isSelected ? 'text-blue-600 dark:text-blue-400' : 'text-gray-500'} cursor-help`} />
                                <div className="absolute left-0 bottom-full mb-2 hidden group-hover:block w-64 p-3 bg-gray-100 dark:bg-slate-800 border border-gray-200 dark:border-slate-700 rounded-lg shadow-xl z-10">
                                  <p className="text-xs text-gray-700 dark:text-gray-300">{tService(addon.tKey + 'Desc')}</p>
                                </div>
                              </div>
                            </div>
                          </div>
                          <span className={`font-bold flex-shrink-0 whitespace-nowrap ml-2 ${isSelected ? 'text-blue-600 dark:text-blue-400' : 'text-gray-600 dark:text-gray-400'}`}>
                            CHF {formatSwiss(addon.price)}
                          </span>
                        </div>
                      </button>
                    );
                  })}
                </div>
              </div>
            </Card>

            {/* Employee Slider (only when service uses per-employee pricing) */}
            {service.hasEmployeeSlider && (
              <Card className="bg-white/80 dark:bg-slate-900/50 border-gray-200 dark:border-slate-800 shadow-xl backdrop-blur-sm">
                <div className="p-6">
                  <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center gap-2">
                      <Users className="w-5 h-5 text-blue-600 dark:text-blue-400" />
                      <h3 className="text-lg font-bold text-gray-900 dark:text-white">{t('calculator.employees')}</h3>
                    </div>
                    <span className="text-2xl font-bold text-blue-600 dark:text-blue-400" data-testid="employees-count">
                      {employees}
                    </span>
                  </div>
                  <input
                    type="range"
                    min="5"
                    max="50"
                    step="5"
                    value={employees}
                    onChange={(e) => setEmployees(parseInt(e.target.value))}
                    className="w-full h-2 bg-gray-200 dark:bg-slate-700 rounded-lg appearance-none cursor-pointer accent-blue-600"
                    style={{
                      background: `linear-gradient(to right, #2563eb 0%, #2563eb ${((employees - 5) / 45) * 100}%, var(--calc-track) ${((employees - 5) / 45) * 100}%, var(--calc-track) 100%)`
                    }}
                    data-testid="employees-slider"
                  />
                  <div className="flex justify-between mt-2 text-xs text-gray-500">
                    <span>5</span>
                    <span>50+</span>
                  </div>
                </div>
              </Card>
            )}

            {/* Trust Signal */}
            <div className="flex items-center gap-3 p-4 bg-gray-100/70 dark:bg-slate-800/30 border border-gray-200 dark:border-slate-700/50 rounded-lg">
              <div className="flex-shrink-0 w-10 h-10 bg-blue-600/20 rounded-full flex items-center justify-center">
                <Check className="w-5 h-5 text-blue-600 dark:text-blue-400" />
              </div>
              <p className="text-sm text-gray-600 dark:text-gray-400">
                <span className="text-gray-900 dark:text-white font-semibold">{t('calculator.trustSignal')}</span>{' '}
                {t('calculator.trustFeatures')}
              </p>
            </div>
          </div>

          {/* RIGHT COL — Summary (Sticky) */}
          <div className="lg:col-span-1">
            <div className="sticky top-24 space-y-4">
              {/* Config Card */}
              <Card className="bg-gradient-to-br from-white to-gray-50 dark:from-slate-900 dark:to-slate-800 border-gray-200 dark:border-slate-700 shadow-2xl">
                <div className="p-6">
                  <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-6">{t('calculator.yourConfig')}</h3>

                  {/* Setup Price + agency savings */}
                  <div className="mb-6 pb-6 border-b border-gray-200 dark:border-slate-700">
                    <div className="text-sm text-gray-600 dark:text-gray-400 mb-2">{t('calculator.oneTimeSetup')}</div>
                    <div className="text-4xl font-bold text-gray-900 dark:text-white" data-testid="setup-price">
                      CHF {formatSwiss(animatedSetup)}
                    </div>
                    <div className="mt-3 flex items-baseline gap-2">
                      <span className="text-xs text-gray-500 line-through">
                        {t('calculator.agencyPrice')}: CHF {formatSwiss(agencyPrice)}
                      </span>
                    </div>
                    <div
                      className="mt-2 inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md bg-emerald-500/10 border border-emerald-500/30"
                      data-testid="savings-badge"
                    >
                      <PiggyBank className="w-3.5 h-3.5 text-emerald-600 dark:text-emerald-400" />
                      <span className="text-xs font-semibold text-emerald-600 dark:text-emerald-400">
                        {t('calculator.youSave')} CHF {formatSwiss(animatedSavings)}
                      </span>
                    </div>
                  </div>

                  {/* Monthly Price */}
                  <div className="mb-6 pb-6 border-b border-gray-200 dark:border-slate-700">
                    <div className="text-sm text-gray-600 dark:text-gray-400 mb-2">{t('calculator.monthlySubscription')}</div>
                    <div className="flex items-baseline gap-2">
                      <span className="text-2xl font-bold text-blue-600 dark:text-blue-400" data-testid="monthly-price">
                        CHF {formatSwiss(animatedMonthly)}.–
                      </span>
                      <span className="text-sm text-gray-500">{t('calculator.perMonth')}</span>
                    </div>
                    <div className="text-xs text-gray-500 mt-1">{t('calculator.hosting')}</div>
                  </div>

                  {/* Delivery */}
                  <div className="flex items-center gap-3 p-3 bg-gray-100 dark:bg-slate-800/50 rounded-lg mb-3">
                    <Clock className="w-5 h-5 text-green-600 dark:text-green-400 flex-shrink-0" />
                    <div>
                      <div className="text-sm font-medium text-gray-900 dark:text-white">{t('calculator.estimatedDelivery')}</div>
                      <div className="text-xs text-gray-600 dark:text-gray-400">
                        {deliveryDays} {t('calculator.businessDays')}
                      </div>
                    </div>
                  </div>

                  {/* Support */}
                  <div className="flex items-center gap-3 p-3 bg-blue-600/10 border border-blue-500/20 rounded-lg mb-6">
                    <Headphones className="w-5 h-5 text-blue-600 dark:text-blue-400 flex-shrink-0" />
                    <div>
                      <div className="text-sm font-medium text-gray-900 dark:text-white">{t('calculator.swissSupport')}</div>
                      <div className="text-xs text-gray-600 dark:text-gray-400">{t('calculator.languages')}</div>
                    </div>
                  </div>

                  <Button
                    onClick={() => setIsOfferModalOpen(true)}
                    data-testid="get-offer-btn"
                    className="w-full bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 text-white font-bold py-6 text-lg shadow-xl shadow-blue-500/30 hover:shadow-blue-500/50 transition-all duration-300 group"
                  >
                    {t('calculator.getOffer')}
                    <ArrowRight className="ml-2 w-5 h-5 group-hover:translate-x-1 transition-transform" />
                  </Button>
                  <p className="text-xs text-center text-gray-500 mt-4">
                    {t('calculator.freeQuote')}
                  </p>
                </div>
              </Card>

              {/* ROI Panel */}
              <Card
                className="bg-gradient-to-br from-emerald-50 to-white dark:from-emerald-950/60 dark:to-slate-900 border-emerald-500/30 shadow-xl"
                data-testid="roi-panel"
              >
                <div className="p-5">
                  <div className="flex items-center gap-2 mb-1">
                    <TrendingUp className="w-4 h-4 text-emerald-600 dark:text-emerald-400" />
                    <h4 className="text-sm font-bold text-gray-900 dark:text-white uppercase tracking-wide">
                      {t('calculator.roiTitle')}
                    </h4>
                  </div>
                  <p className="text-[11px] text-gray-500 mb-4">{t('calculator.roiSubtitle')}</p>
                  <div className="space-y-3">
                    <div className="flex items-center justify-between">
                      <span className="text-xs text-gray-600 dark:text-gray-400">{t('calculator.roiHours')}</span>
                      <span className="text-sm font-bold text-emerald-600 dark:text-emerald-400" data-testid="roi-hours">
                        {formatSwiss(animatedHours)} h
                      </span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-xs text-gray-600 dark:text-gray-400">{t('calculator.roiMoney')}</span>
                      <span className="text-sm font-bold text-emerald-600 dark:text-emerald-400" data-testid="roi-money">
                        CHF {formatSwiss(animatedMoney)}
                      </span>
                    </div>
                    <div className="flex items-center justify-between pt-3 border-t border-emerald-500/20">
                      <span className="text-xs text-gray-700 dark:text-gray-300 font-medium">
                        {t('calculator.roiPayback')}
                      </span>
                      <span className="text-lg font-bold text-gray-900 dark:text-white" data-testid="roi-payback">
                        {paybackMonths} {t('calculator.roiMonths')}
                      </span>
                    </div>
                  </div>
                </div>
              </Card>

              {/* Value Props */}
              <div className="space-y-2 pt-2">
                {[
                  { id: 'swiss-dev', text: t('calculator.valueProps.swiss') },
                  { id: 'gdpr', text: t('calculator.valueProps.gdpr') },
                  { id: 'source-code', text: t('calculator.valueProps.sourceCode') },
                  { id: 'updates', text: t('calculator.valueProps.updates') }
                ].map((prop) => (
                  <div key={prop.id} className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-400">
                    <Check className="w-4 h-4 text-green-600 dark:text-green-400" />
                    <span>{prop.text}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>

      <OfferModal
        isOpen={isOfferModalOpen}
        onClose={() => setIsOfferModalOpen(false)}
        configuration={getConfiguration()}
      />
    </section>
  );
};

export default PremiumCalculator;
